// Attendance → Location Tracking. Live staff map, employee routes, location
// history, devices, settings and audit for the Dnexus Staff Android app. Access is
// enforced server-side (admins, or users granted the "location_tracking" page).
import { useEffect, useState } from "react";
import { ShieldOff, MapPinned, Route as RouteIcon, TableProperties, Smartphone, Settings2, ScrollText, Loader2 } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { useBusiness } from "@/contexts/BusinessContext";
import { ltApi } from "./ltShared";
import LiveMapTab from "./LiveMapTab";
import RouteTab from "./RouteTab";
import HistoryTab from "./HistoryTab";
import { DevicesTab, SettingsTab, AuditTab } from "./AdminTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  ["live", "Live Map", MapPinned], ["route", "Employee Route", RouteIcon], ["history", "Location History", TableProperties],
  ["devices", "Devices", Smartphone], ["settings", "Settings", Settings2], ["audit", "Audit Log", ScrollText],
];

export default function LocationTracking() {
  const { businessId } = useBusiness();
  const [me, setMe] = useState(null);
  const [access, setAccess] = useState("checking"); // checking | ok | denied
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("live");
  const [routeUser, setRouteUser] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setMe).catch(() => {});
  }, []);
  useEffect(() => {
    setAccess("checking");
    ltApi("/overview", { businessId })
      .then((d) => { setEmployees(d.employees.map(({ user_id, name, employee_id, department, designation }) => ({ user_id, name, employee_id, department, designation }))); setAccess("ok"); })
      .catch((e) => setAccess(e.status === 403 ? "denied" : "ok"));
  }, [businessId]);

  const isAdmin = !!me && ["super_admin", "admin"].includes(me.role);
  const viewRoute = (uid) => { setRouteUser(uid); setTab("route"); };

  if (access === "checking") return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (access === "denied") {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <ShieldOff className="mx-auto h-10 w-10 text-slate-400" />
        <h2 className="mt-3 text-lg font-semibold text-slate-900">No access to Location Tracking</h2>
        <p className="mt-1 text-sm text-slate-500">Staff location data is restricted. Ask an administrator to grant you the “Location Tracking” page in Access Control.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="location-tracking-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Location Tracking</h1>
          <p className="text-sm text-slate-500">Live staff locations and routes from the Dnexus Staff app — recorded only between Punch In and Punch Out.</p>
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`lt-tab-${k}`}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition ${tab === k ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>
      {tab === "live" && <LiveMapTab businessId={businessId} isAdmin={isAdmin} onViewRoute={viewRoute} />}
      {tab === "route" && <RouteTab businessId={businessId} employees={employees} initialUserId={routeUser} />}
      {tab === "history" && <HistoryTab businessId={businessId} employees={employees} />}
      {tab === "devices" && <DevicesTab businessId={businessId} isAdmin={isAdmin} />}
      {tab === "settings" && <SettingsTab businessId={businessId} isAdmin={isAdmin} />}
      {tab === "audit" && <AuditTab businessId={businessId} />}
    </div>
  );
}
